<template>
  <div
    :class="['value-display', size, { subtle }]"
    v-tooltip="{ content: tooltipContent, delay: { show: 1000, hide: 100 }, placement: 'bottom' }"
    @click="handleClick"
  >
    <template v-if="estimated">
      <stat-item label="Estimated Value" :size="size" :value="value" />
    </template>

    <template v-else>
      <value-logo />

      <align no-gap>
        <icon v-if="shouldBlurValue" class="blur-icon" name="visibility" />
        <span v-else class="price" :class="{ loading }">
          {{ value }}
        </span>

        <external-link href="https://cardladder.zendesk.com/hc/en-us/articles/11943684520471--Card-Ladder-Value-The-Intersection-of-Player-Indexes-Price-Modeling" />
      </align>
    </template>

    <span v-show="confidence && value !== 'N/A'" :class="['confidence-bars', `confidence-${confidence}`]">
      <span class="bar active" />
      <span :class="['bar', { active: confidence >= 2 }]" />
      <span :class="['bar', { active: confidence >= 3 }]" />
      <span :class="['bar', { active: confidence >= 4 }]" />
      <span :class="['bar', { active: confidence >= 5 }]" />
    </span>
  </div>
</template>

<script>
import Align from './Align';
import ExternalLink from './ExternalLink';
import StatItem from './StatItem';
import ValueLogo from './ValueLogo';

export default {
  components: {
    Align,
    ExternalLink,
    StatItem,
    ValueLogo,
  },

  props: {
    confidence: Number,
    value: String,
    estimated: Boolean,
    subtle: Boolean,
    blur: Boolean,
    loading: Boolean,
    size: {
      type: String,
      default: 'regular',
    },
  },

  data() {
    return {
      shouldBlurValue: this.blur || false,
    };
  },

  watch: {
    blur: {
      immediate: true,
      handler(to) {
        this.shouldBlurValue = Boolean(to);
      },
    },
  },

  computed: {
    tooltipContent() {
      return this.estimated
        ? `Estimated Value seeks to reflect the card's current market price. It is determined by anchoring the card's last sold price to the player's total market index. As the index fluctuates from day to day, so does the card's Estimated Value.`
        : `CL Value seeks to reflect the card's current market price. It is determined by anchoring the card's last sold price to the player's total market index. As the index fluctuates from day to day, so does the card's CL Value. This particular CL Value has a confidence level of ${this.confidence}/5. The more recent the last sold price, the higher the confidence level.`;
    },
  },

  methods: {
    handleClick() {
      this.shouldBlurValue = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.value-display {
  display: inline-grid;
  --external-link-icon-size: 12px;

  &.large {
    --value-logo-size: 18px;

    .price {
      font-size: 32px;
    }
  }

  &.subtle {
    padding-top: 3px;
    --value-logo-size: 9px;
  }
}

.blur-icon {
  color: var(--theme-inactive);
  --icon-size: 26px;
  margin-left: 20px;
  cursor: pointer;
}

.price {
  font-weight: bold;
  font-family: $font-family-secondary;
  line-height: 1.6;
  font-size: 16px;

  &.loading {
    opacity: 0;
  }

  &.confidence-1 {
    color: $color-confidence-1;
  }

  &.confidence-2 {
    color: $color-confidence-2;
  }

  &.confidence-3 {
    color: $color-confidence-3;
  }

  &.confidence-4 {
    color: $color-confidence-4;
  }

  &.confidence-5 {
    color: $color-confidence-5;
  }
}

.confidence-bars {
  display: flex;
  align-items: center;

  > .bar:not(:last-child) {
    margin-right: 1px;
  }

  &.confidence-1 {
    .bar.active {
      background-color: $color-confidence-1;
    }
  }

  &.confidence-2 {
    .bar.active {
      background-color: $color-confidence-2;
    }
  }

  &.confidence-3 {
    .bar.active {
      background-color: $color-confidence-3;
    }
  }

  &.confidence-4 {
    .bar.active {
      background-color: $color-confidence-4;
    }
  }

  &.confidence-5 {
    .bar.active {
      background-color: $color-confidence-5;
    }
  }
}

.bar {
  height: 2px;
  width: 20px;
  border-radius: $border-radius;
  background-color: var(--theme-inactive);
}
</style>
